import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, trackClick, trackScroll } from '../api/analyticsApi';

/**
 * Drop this hook into App.js to automatically track:
 * - Page views on every route change
 * - Click positions (sampled)
 * - Max scroll depth on route change
 */
export default function useAnalyticsTracker() {
  const location = useLocation();
  const scrollRef = useRef(0);
  const pathRef = useRef('');

  // Track page view on route change
  useEffect(() => {
    // Flush previous page's scroll depth
    if (pathRef.current && scrollRef.current > 0) {
      trackScroll(pathRef.current, scrollRef.current);
    }
    scrollRef.current = 0;
    pathRef.current = location.pathname;
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Track scroll depth
  useEffect(() => {
    const onScroll = () => {
      const depth = Math.round(
        ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
      );
      if (depth > scrollRef.current) scrollRef.current = depth;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Track clicks (sample: 1 in 3)
  useEffect(() => {
    let count = 0;
    const onClick = (e) => {
      count++;
      if (count % 3 !== 0) return; // sample
      const tag = e.target.tagName?.toLowerCase() || '';
      const cls = e.target.className?.toString().slice(0, 60) || '';
      trackClick(
        location.pathname,
        e.clientX,
        e.clientY,
        `${tag}.${cls}`
      );
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [location.pathname]);

  // Flush scroll depth on unload
  useEffect(() => {
    const onUnload = () => {
      if (pathRef.current && scrollRef.current > 0) {
        trackScroll(pathRef.current, scrollRef.current);
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);
}
